import React from 'react';
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  LinearProgress,
  CircularProgress,
  Chip,
  Paper,
  Grid,
  Card,
  CardContent
} from '@mui/material';
import {
  LocationOn,
  Analytics,
  CheckCircle,
  Error
} from '@mui/icons-material';

const RankingProgressOverlay = ({ 
  open, 
  onClose, 
  progressData = null, 
  keyword = '' 
}) => {
  if (!progressData) {
    return (
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            minHeight: '400px'
          }
        }}
      >
        <DialogContent sx={{ p: 4 }}>
          <Box display="flex" flexDirection="column" alignItems="center" gap={3}>
            <CircularProgress size={60} thickness={4} />
            <Typography variant="h6" color="primary" fontWeight={600}>
              Ranking-Analyse wird gestartet...
            </Typography>
            <Typography variant="body2" color="textSecondary" textAlign="center">
              Die Analyse für das Keyword "{keyword}" wird vorbereitet.
              <br />
              Bitte haben Sie einen Moment Geduld.
            </Typography>
          </Box>
        </DialogContent>
      </Dialog>
    );
  }

  const { 
    phase, 
    currentPin, 
    totalPins, 
    locationName, 
    coordinates, 
    message, 
    error 
  } = progressData;

  const progressPercentage = totalPins > 0 ? (currentPin / totalPins) * 100 : 0;

  // Different layouts based on phase
  if (phase === 'starting') {
    return (
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            minHeight: '400px'
          }
        }}
      >
        <DialogContent sx={{ p: 4 }}>
          <Box display="flex" flexDirection="column" alignItems="center" gap={3}>
            <Box sx={{ position: 'relative', display: 'inline-flex' }}>
              <CircularProgress size={80} thickness={4} />
              <Box
                sx={{
                  top: 0,
                  left: 0,
                  bottom: 0,
                  right: 0,
                  position: 'absolute',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Analytics sx={{ fontSize: 32, color: 'primary.main' }} />
              </Box>
            </Box>
            
            <Typography variant="h5" color="primary" fontWeight={600}>
              Ranking-Analyse startet
            </Typography>
            
            <Typography variant="body1" color="textSecondary" textAlign="center">
              Bereite 18-Punkt-Analyse für "{keyword}" vor...
            </Typography>
            
            <Paper 
              elevation={0} 
              sx={{ 
                p: 2, 
                backgroundColor: 'background.default',
                borderRadius: 2,
                width: '100%',
                maxWidth: 400
              }}
            >
              <Typography variant="caption" color="textSecondary" display="block" mb={1}>
                Analysiert werden:
              </Typography>
              <Grid container spacing={1}>
                <Grid item xs={4}>
                  <Chip 
                    label="1 Zentrum" 
                    size="small" 
                    color="primary" 
                    variant="outlined"
                  />
                </Grid>
                <Grid item xs={4}>
                  <Chip 
                    label="6 Innere" 
                    size="small" 
                    color="secondary" 
                    variant="outlined"
                  />
                </Grid>
                <Grid item xs={4}>
                  <Chip 
                    label="12 Äußere" 
                    size="small" 
                    color="info" 
                    variant="outlined"
                  />
                </Grid>
              </Grid>
            </Paper>
          </Box>
        </DialogContent>
      </Dialog>
    );
  }

  if (phase === 'analyzing') {
    return (
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            minHeight: '500px'
          }
        }}
      >
        <DialogContent sx={{ p: 4 }}>
          {/* Header */}
          <Box mb={4}>
            <Typography variant="h5" fontWeight={600} gutterBottom>
              Ranking-Analyse: "{keyword}"
            </Typography>
            <Typography variant="body2" color="textSecondary">
              18-Punkt-Concentric-Circle-Analyse mit SERP API
            </Typography>
          </Box>

          {/* Progress Bar */}
          <Box mb={4}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
              <Typography variant="h6" color="primary" fontWeight={600}>
                Pin {currentPin} / {totalPins}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {progressPercentage.toFixed(0)}% abgeschlossen
              </Typography>
            </Box>
            
            <LinearProgress
              variant="determinate"
              value={progressPercentage}
              sx={{
                height: 8,
                borderRadius: 4,
                backgroundColor: 'grey.200',
                '& .MuiLinearProgress-bar': {
                  borderRadius: 4,
                }
              }}
            />
          </Box>

          {/* Current Location */}
          <Card elevation={2} sx={{ mb: 3, border: '2px solid', borderColor: 'primary.main' }}>
            <CardContent sx={{ p: 3 }}>
              <Box display="flex" alignItems="center" gap={2} mb={2}>
                <Box sx={{ position: 'relative' }}>
                  <CircularProgress size={24} thickness={4} />
                  <LocationOn 
                    sx={{ 
                      position: 'absolute', 
                      top: '50%', 
                      left: '50%', 
                      transform: 'translate(-50%, -50%)',
                      fontSize: 12,
                      color: 'primary.main'
                    }} 
                  />
                </Box>
                <Box>
                  <Typography variant="h6" fontWeight={600}>
                    {locationName || 'Standort wird analysiert...'}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {message || 'API-Anfrage wird verarbeitet...'}
                  </Typography>
                  {coordinates && (
                    <Typography variant="caption" color="textSecondary">
                      Koordinaten: {coordinates.lat.toFixed(4)}, {coordinates.lng.toFixed(4)}
                    </Typography>
                  )}
                </Box>
              </Box>
              
              {error && (
                <Box display="flex" alignItems="center" gap={1} mt={2}>
                  <Error color="error" sx={{ fontSize: 16 }} />
                  <Typography variant="caption" color="error">
                    {error}
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>

          {/* Location Grid Preview */}
          <Paper elevation={0} sx={{ p: 3, backgroundColor: 'background.default', borderRadius: 2 }}>
            <Typography variant="subtitle2" gutterBottom color="textSecondary">
              Analysierte Standorte:
            </Typography>
            
            <Grid container spacing={1}>
              {Array.from({ length: totalPins }, (_, index) => {
                const pinNumber = index + 1;
                const isCompleted = pinNumber < currentPin;
                const isCurrent = pinNumber === currentPin;
                const isPending = pinNumber > currentPin;
                
                let circleType = 'center';
                if (pinNumber > 1 && pinNumber <= 7) circleType = 'inner';
                if (pinNumber > 7) circleType = 'outer';
                
                let color = 'default';
                if (circleType === 'center') color = 'primary';
                if (circleType === 'inner') color = 'secondary';
                if (circleType === 'outer') color = 'info';
                
                return (
                  <Grid item key={pinNumber}>
                    <Chip
                      label={pinNumber}
                      size="small"
                      color={color}
                      variant={
                        isCompleted ? 'filled' :
                        isCurrent ? 'filled' :
                        'outlined'
                      }
                      icon={isCompleted ? <CheckCircle sx={{ fontSize: 14 }} /> : undefined}
                      sx={{
                        minWidth: 36,
                        fontWeight: isCurrent ? 'bold' : 'normal',
                        opacity: isPending ? 0.5 : 1,
                        transition: 'all 0.3s ease'
                      }}
                    />
                  </Grid>
                );
              })}
            </Grid>
            
            <Box mt={2} display="flex" gap={2} flexWrap="wrap">
              <Box display="flex" alignItems="center" gap={0.5}>
                <Chip label="1" size="small" color="primary" />
                <Typography variant="caption">Zentrum</Typography>
              </Box>
              <Box display="flex" alignItems="center" gap={0.5}>
                <Chip label="2-7" size="small" color="secondary" />
                <Typography variant="caption">Innerer Kreis (7.5km)</Typography>
              </Box>
              <Box display="flex" alignItems="center" gap={0.5}>
                <Chip label="8-18" size="small" color="info" />
                <Typography variant="caption">Äußerer Kreis (15km)</Typography>
              </Box>
            </Box>
          </Paper>

          {/* Performance Info */}
          <Box mt={3} display="flex" justify="space-between" alignItems="center">
            <Typography variant="caption" color="textSecondary">
              ⚡ Parallele Verarbeitung aktiv • 5 gleichzeitige API-Aufrufe
            </Typography>
            <Typography variant="caption" color="textSecondary">
              🔄 Geschätzte Restzeit: {Math.max(0, ((totalPins - currentPin) / 5) * 2).toFixed(0)}s
            </Typography>
          </Box>
        </DialogContent>
      </Dialog>
    );
  }

  // Completed phase
  if (phase === 'completed') {
    return (
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            minHeight: '300px'
          }
        }}
      >
        <DialogContent sx={{ p: 4 }}>
          <Box display="flex" flexDirection="column" alignItems="center" gap={3}>
            <CheckCircle sx={{ fontSize: 80, color: 'success.main' }} />
            
            <Typography variant="h5" color="success.main" fontWeight={600}>
              Analyse abgeschlossen!
            </Typography>
            
            <Typography variant="body1" color="textSecondary" textAlign="center">
              Die Ranking-Analyse für "{keyword}" wurde erfolgreich durchgeführt.
              <br />
              Alle 18 Standorte wurden analysiert.
            </Typography>
            
            <Paper 
              elevation={0} 
              sx={{ 
                p: 2, 
                backgroundColor: 'success.50',
                borderRadius: 2,
                width: '100%',
                maxWidth: 400
              }}
            >
              <Typography variant="caption" color="success.dark" display="block" mb={1}>
                Analyseergebnisse:
              </Typography>
              <Typography variant="body2" color="success.dark">
                ✅ {totalPins} Standorte erfolgreich analysiert
                <br />
                📊 Ranking-Daten aktualisiert
                <br />
                💾 Automatischer Snapshot erstellt
              </Typography>
            </Paper>
          </Box>
        </DialogContent>
      </Dialog>
    );
  }

  // Default/error state
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
    >
      <DialogContent>
        <Box textAlign="center" py={4}>
          <Typography variant="h6">
            Ranking-Analyse läuft...
          </Typography>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default RankingProgressOverlay;